// src/components/accountability/AccountabilityDashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Users,
  AlertTriangle,
  Clock,
  MapPin,
  CheckCircle,
  Filter,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  locationService,
  FlaggedEntry,
  CanvasserPerformanceMetrics,
  CanvasserLocationUpdate,
} from '../../services/location.service';
import { Timestamp } from 'firebase/firestore';

interface AccountabilityDashboardProps {
  projectId?: string;
}

const AccountabilityDashboard: React.FC<AccountabilityDashboardProps> = ({ projectId }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'flags' | 'activity'>('overview');
  const [flaggedEntries, setFlaggedEntries] = useState<FlaggedEntry[]>([]);
  const [canvasserLocations, setCanvasserLocations] = useState<CanvasserLocationUpdate[]>([]);
  const [selectedCanvasser, setSelectedCanvasser] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<CanvasserPerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    severity: 'all' as 'all' | 'low' | 'medium' | 'high',
    reviewed: 'all' as 'all' | 'reviewed' | 'unreviewed',
  });

  useEffect(() => {
    loadFlaggedEntries();
    const unsubscribe = locationService.subscribeToCanvasserLocations(setCanvasserLocations);
    return () => unsubscribe();
  }, []);

  const loadFlaggedEntries = async () => {
    setLoading(true);
    try {
      const entries = await locationService.getFlaggedEntries({
        severity: filters.severity !== 'all' ? filters.severity : undefined,
        reviewed: filters.reviewed === 'all' ? undefined : filters.reviewed === 'reviewed',
      });
      setFlaggedEntries(entries);
    } catch (error) {
      console.error('Failed to load flagged entries:', error);
    }
    setLoading(false);
  };

  const loadCanvasserMetrics = async (userId: string, userName: string) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    try {
      const metrics = await locationService.getCanvasserPerformanceMetrics(
        userId,
        userName,
        startDate,
        endDate
      );
      setPerformanceMetrics(metrics);
      setSelectedCanvasser(userId);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  };

  const handleReviewFlag = async (flagId: string, resolution: string) => {
    try {
      await locationService.reviewFlag(flagId, 'current-user', resolution);
      loadFlaggedEntries();
    } catch (error) {
      console.error('Failed to review flag:', error);
    }
  };

  const getSeverityBadge = (severity: 'low' | 'medium' | 'high') => {
    const colors = {
      low: 'bg-yellow-100 text-yellow-700',
      medium: 'bg-orange-100 text-orange-700',
      high: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[severity]}`}>
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </span>
    );
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleString();
  };

  const unreviewedCount = flaggedEntries.filter((f) => !f.reviewed).length;
  const highSeverityCount = flaggedEntries.filter((f) => f.severity === 'high' && !f.reviewed).length;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{canvasserLocations.length}</p>
              <p className="text-sm text-gray-500">Active Canvassers</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{unreviewedCount}</p>
              <p className="text-sm text-gray-500">Unreviewed Flags</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{highSeverityCount}</p>
              <p className="text-sm text-gray-500">High Priority</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {flaggedEntries.filter((f) => f.reviewed).length}
              </p>
              <p className="text-sm text-gray-500">Reviewed Today</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex -mb-px">
            {(['overview', 'flags', 'activity'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'overview' && 'Live Overview'}
                {tab === 'flags' && `Flagged Entries (${unreviewedCount})`}
                {tab === 'activity' && 'Activity Timeline'}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4">
          {/* Live Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Active Canvassers</h3>
                <button
                  onClick={() => {}}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {canvasserLocations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No active canvassers at the moment</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {canvasserLocations.map((canvasser) => (
                    <div
                      key={canvasser.userId}
                      className="border rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                      onClick={() => loadCanvasserMetrics(canvasser.userId, canvasser.userName)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{canvasser.userName}</h4>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            canvasser.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {canvasser.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {canvasser.location.lat.toFixed(4)}, {canvasser.location.lng.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>Last update: {formatTimestamp(canvasser.lastActivityAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Performance Metrics Modal */}
              {selectedCanvasser && performanceMetrics && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="text-lg font-medium">{performanceMetrics.userName} - 7 Day Metrics</h3>
                      <button
                        onClick={() => setSelectedCanvasser(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceMetrics.totalDispositions}
                          </p>
                          <p className="text-sm text-gray-500">Total Dispositions</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceMetrics.surveysCompleted}
                          </p>
                          <p className="text-sm text-gray-500">Surveys Completed</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceMetrics.averageTimePerAddress}s
                          </p>
                          <p className="text-sm text-gray-500">Avg Time/Address</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceMetrics.hoursActive}h
                          </p>
                          <p className="text-sm text-gray-500">Hours Active</p>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Accountability</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Location Mismatches</span>
                            <span
                              className={`font-medium ${
                                performanceMetrics.locationMismatchRate > 10
                                  ? 'text-red-600'
                                  : 'text-green-600'
                              }`}
                            >
                              {performanceMetrics.locationMismatches} ({performanceMetrics.locationMismatchRate}%)
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Rapid Submissions</span>
                            <span
                              className={`font-medium ${
                                performanceMetrics.rapidSubmissionRate > 10
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                              }`}
                            >
                              {performanceMetrics.rapidSubmissions} ({performanceMetrics.rapidSubmissionRate}%)
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Distinct Locations</span>
                            <span className="font-medium text-gray-900">
                              {performanceMetrics.distinctLocationsVisited}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Flagged Entries Tab */}
          {activeTab === 'flags' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <button
                  onClick={loadFlaggedEntries}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {showFilters && (
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Severity</label>
                    <select
                      value={filters.severity}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, severity: e.target.value as typeof filters.severity }))
                      }
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="all">All</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Status</label>
                    <select
                      value={filters.reviewed}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, reviewed: e.target.value as typeof filters.reviewed }))
                      }
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="all">All</option>
                      <option value="unreviewed">Unreviewed</option>
                      <option value="reviewed">Reviewed</option>
                    </select>
                  </div>
                  <button
                    onClick={loadFlaggedEntries}
                    className="mt-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : flaggedEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p>No flagged entries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {flaggedEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className={`border rounded-lg p-4 ${entry.reviewed ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getSeverityBadge(entry.severity)}
                            <span className="text-sm text-gray-500">{entry.flagType.replace('_', ' ')}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-900">{entry.userName}</p>
                          <p className="text-sm text-gray-600">{entry.description}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatTimestamp(entry.timestamp)}</p>
                        </div>
                        {!entry.reviewed && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleReviewFlag(entry.id!, 'Reviewed and acknowledged')}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                              <Eye className="w-3 h-3" />
                              Review
                            </button>
                          </div>
                        )}
                      </div>
                      {entry.reviewed && entry.resolution && (
                        <div className="mt-2 pt-2 border-t text-xs text-gray-500">
                          <span className="font-medium">Resolution:</span> {entry.resolution}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity Timeline Tab */}
          {activeTab === 'activity' && (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Activity timeline coming soon</p>
              <p className="text-sm">Select a canvasser from the Live Overview tab to view their metrics</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountabilityDashboard;
